import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mountain, TrendingUp, Zap } from "lucide-react";

interface DifficultyBadgeProps {
  niveau: number;
  showTooltip?: boolean;
  size?: "sm" | "md" | "lg";
}

export const DifficultyBadge = ({ niveau, showTooltip = true, size = "md" }: DifficultyBadgeProps) => {
  const difficultyConfig = {
    1: {
      label: "Facile",
      icon: Mountain,
      color: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
      description: "Accessible à tous",
      details: "Rythme tranquille, peu d'effort physique requis. Convient aux familles et personnes de tous âges."
    },
    2: {
      label: "Modéré",
      icon: TrendingUp,
      color: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20",
      description: "Condition physique correcte",
      details: "2-3h de marche possible, dénivelé modéré (jusqu'à 300m). Quelques passages techniques."
    },
    3: {
      label: "Difficile",
      icon: Zap,
      color: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
      description: "Bonne condition physique requise",
      details: "Plus de 3h de marche, dénivelé important (plus de 300m). Passages techniques et effort soutenu."
    }
  };

  const config = difficultyConfig[niveau as keyof typeof difficultyConfig] || difficultyConfig[1];
  const Icon = config.icon;

  const iconSize = size === "sm" ? "h-3 w-3" : size === "md" ? "h-4 w-4" : "h-5 w-5";
  const textSize = size === "sm" ? "text-xs" : size === "md" ? "text-sm" : "text-base";
  const padding = size === "sm" ? "px-2 py-0.5" : size === "md" ? "px-3 py-1" : "px-4 py-1.5";

  const badgeContent = (
    <Badge className={`${config.color} ${padding} ${textSize} font-semibold border`}>
      <Icon className={`${iconSize} mr-1.5`} />
      {config.label}
    </Badge>
  );

  if (!showTooltip) {
    return badgeContent;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">{config.description}</p>
            <p className="text-xs text-muted-foreground">{config.details}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
